import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Plus,
  Edit,
  Trash2,
  Route,
  Mail,
  User,
  Users,
  Bus,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
  RefreshCw,
  Key,
  Eye,
  EyeOff
} from "lucide-react";
import { API_ENDPOINTS } from "../../constants/config";
import axiosInstance from "../../utils/axiosInstance";

const formatPersonName = (person) => {
  if (!person) return "";
  return `${person.firstName}${person.middleName ? ` ${person.middleName}` : ""} ${person.lastName}`;
};

const VehicleManagement = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ 
    number: "", 
    type: "bus", 
    email: "",
    driver: "none",
    conductor: "none"
  });
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editForm, setEditForm] = useState({ 
    number: "", 
    type: "bus", 
    email: "",
    driver: "none",
    conductor: "none"
  });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [changingPasswordVehicle, setChangingPasswordVehicle] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const fetchVehicles = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.vehicles);
      setVehicles(res.data.data.vehicles || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch vehicles");
      console.error("Error fetching vehicles:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.drivers);
      setDrivers(res.data.data.drivers || []);
    } catch (err) {
      console.error("Error fetching drivers:", err);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const schoolId = user?.school;
      if (!schoolId) {
        setError("No school ID found for current user.");
        return;
      }
      
      const vehicleData = {
        vehicleNumber: form.number,
        type: form.type,
        email: form.email,
        school: schoolId
      };

      if (form.driver && form.driver !== "none") {
        vehicleData.driver = form.driver;
      }
      if (form.conductor && form.conductor !== "none") {
        vehicleData.conductor = form.conductor;
      }

      await axiosInstance.post(API_ENDPOINTS.vehicles, vehicleData);
      setForm({ number: "", type: "bus", email: "", driver: "none", conductor: "none" });
      setAddModalOpen(false);
      setError("");
      fetchVehicles();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to create vehicle. Please try again.";
      setError(errorMessage);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setEditForm({
      number: vehicle.vehicleNumber,
      type: vehicle.type,
      email: vehicle.email || "",
      driver: vehicle.driver?._id || "none",
      conductor: vehicle.conductor?._id || "none"
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingVehicle) return;
    
    setSaving(true);
    setError("");
    try {
      const updateData = {
        vehicleNumber: editForm.number,
        type: editForm.type,
        email: editForm.email
      };

      if (editForm.driver && editForm.driver !== "none") {
        updateData.driver = editForm.driver;
      }
      if (editForm.conductor && editForm.conductor !== "none") {
        updateData.conductor = editForm.conductor;
      }

      await axiosInstance.put(API_ENDPOINTS.vehicle(editingVehicle._id), updateData);
      setEditModalOpen(false);
      setEditingVehicle(null);
      fetchVehicles();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to update vehicle. Please try again.";
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleManageRoute = (vehicle) => {
    navigate(`/dashboard/vehicles/${vehicle._id}/edit`);
  };

  const handleDelete = async (id, vehicleNumber) => {
    if (!window.confirm(`Delete vehicle ${vehicleNumber}?`)) return;
    try {
      await axiosInstance.delete(API_ENDPOINTS.vehicle(id));
      fetchVehicles();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to delete vehicle. Please try again.";
      setError(errorMessage);
    }
  };

  const handleChangePassword = (vehicle) => {
    setChangingPasswordVehicle(vehicle);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordModalOpen(true);
  };

  const handleSavePassword = async () => {
    if (!changingPasswordVehicle) return;

    setPasswordError("");
    
    if (!newPassword || newPassword.trim() === "") {
      setPasswordError("Password is required");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await axiosInstance.post(
        API_ENDPOINTS.vehicleChangePassword(changingPasswordVehicle._id),
        { newPassword }
      );
      setPasswordModalOpen(false);
      setChangingPasswordVehicle(null);
      setNewPassword("");
      setConfirmPassword("");
      setPasswordError("");
      // Show success message
      setError("");
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to change password. Please try again.";
      setPasswordError(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      vehicle.vehicleNumber?.toLowerCase().includes(search) ||
      vehicle.username?.toLowerCase().includes(search) ||
      vehicle.type?.toLowerCase().includes(search) ||
      vehicle.email?.toLowerCase().includes(search) ||
      formatPersonName(vehicle.driver)?.toLowerCase().includes(search) ||
      formatPersonName(vehicle.conductor)?.toLowerCase().includes(search)
    );
  });

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold mb-2 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent flex items-center gap-3">
                  <Bus className="h-6 w-6 text-primary" />
                  Vehicle Management
                </h3>
                <p className="text-sm text-muted-foreground">
                  Manage all vehicles, drivers, and conductors
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={fetchVehicles} 
                      variant="outline" 
                      size="icon"
                      className="h-11 w-11 border-2 hover:border-primary transition-colors"
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Refresh vehicles</p>
                  </TooltipContent>
                </Tooltip>
                <Button 
                  onClick={() => setAddModalOpen(true)} 
                  variant="default" 
                  className="h-11 bg-[#0263cc] hover:bg-[#0263cc]/90 text-white font-semibold shadow-lg hover:shadow-xl transition-all gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Vehicle
                </Button>
              </div>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by number, username, type, email, driver, or conductor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-2 focus:border-primary transition-colors"
              />
            </div>

            {error && (
              <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col justify-center items-center h-64 gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-muted-foreground font-medium">Loading vehicles...</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border-2">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Vehicle Number</TableHead>
                      <TableHead className="font-semibold">Username</TableHead>
                      <TableHead className="font-semibold">Type</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Driver</TableHead>
                      <TableHead className="font-semibold">Conductor</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVehicles.map(vehicle => (
                      <TableRow key={vehicle._id} className="hover:bg-muted/30 transition-colors">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Bus className="h-4 w-4 text-primary" />
                            {vehicle.vehicleNumber}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Key className="h-3 w-3 text-muted-foreground" />
                            <span className="font-mono text-sm">{vehicle.username || "-"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                            {vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            {vehicle.email || "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {vehicle.driver ? (
                            <div className="flex items-center gap-2">
                              <User className="h-3 w-3 text-muted-foreground" />
                              <span>{formatPersonName(vehicle.driver)}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic">No Driver</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {vehicle.conductor ? (
                            <div className="flex items-center gap-2">
                              <Users className="h-3 w-3 text-muted-foreground" />
                              <span>{formatPersonName(vehicle.conductor)}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic">No Conductor</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 justify-end">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="icon" 
                                  variant="outline" 
                                  onClick={() => handleEdit(vehicle)}
                                  className="h-9 w-9 border-2 hover:border-primary"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Edit vehicle</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="icon" 
                                  variant="outline" 
                                  onClick={() => handleChangePassword(vehicle)}
                                  className="h-9 w-9 border-2 hover:border-green-500 hover:text-green-500"
                                >
                                  <Key className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Change password</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="icon" 
                                  variant="outline" 
                                  onClick={() => handleManageRoute(vehicle)}
                                  className="h-9 w-9 border-2 hover:border-blue-500 hover:text-blue-500"
                                >
                                  <Route className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Manage route</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  size="icon" 
                                  variant="outline" 
                                  onClick={() => handleDelete(vehicle._id, vehicle.vehicleNumber)}
                                  className="h-9 w-9 border-2 text-red-500 hover:bg-red-50 hover:border-red-500"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Delete vehicle</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredVehicles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <Bus className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
                          <p className="text-lg font-medium text-muted-foreground">
                            {searchTerm ? "No vehicles match your search" : "No vehicles found"}
                          </p>
                          {!searchTerm && (
                            <p className="text-sm text-muted-foreground mt-2">Create your first vehicle to get started</p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={addModalOpen} onOpenChange={(open) => {
          setAddModalOpen(open);
          if (!open) {
            setForm({ number: "", type: "bus", email: "", driver: "none", conductor: "none" });
            setError("");
          }
        }}>
          <DialogContent className="max-w-2xl border-2 shadow-xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Add New Vehicle
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="number" className="text-sm font-semibold flex items-center gap-2">
                    <Bus className="h-4 w-4" />
                    Vehicle Number
                  </Label>
                  <Input
                    id="number"
                    name="number"
                    value={form.number}
                    onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                    className="h-11 border-2 focus:border-primary transition-colors"
                    placeholder="e.g., BUS-001"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="h-11 border-2 focus:border-primary transition-colors"
                    placeholder="driver@school.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-sm font-semibold">Type</Label>
                  <Select
                    name="type"
                    value={form.type}
                    onValueChange={value => setForm(f => ({ ...f, type: value }))}
                  >
                    <SelectTrigger className="h-11 border-2 focus:border-primary transition-colors">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bus">Bus</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver" className="text-sm font-semibold flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Driver (Optional)
                  </Label>
                  <Select 
                    value={form.driver} 
                    onValueChange={(value) => setForm(f => ({ ...f, driver: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger className="h-11 border-2 focus:border-primary transition-colors">
                      <SelectValue placeholder="Select a driver" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Driver</SelectItem>
                      {drivers
                        .filter(d => d.role === "driver")
                        .map(driver => (
                          <SelectItem key={driver._id} value={driver._id}>
                            {formatPersonName(driver)}
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="conductor" className="text-sm font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Conductor (Optional)
                  </Label>
                  <Select 
                    value={form.conductor} 
                    onValueChange={(value) => setForm(f => ({ ...f, conductor: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger className="h-11 border-2 focus:border-primary transition-colors">
                      <SelectValue placeholder="Select a conductor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Conductor</SelectItem>
                      {drivers
                        .filter(d => d.role === "conductor")
                        .map(conductor => (
                          <SelectItem key={conductor._id} value={conductor._id}>
                            {formatPersonName(conductor)}
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setAddModalOpen(false);
                    setForm({ number: "", type: "bus", email: "", driver: "none", conductor: "none" });
                    setError("");
                  }}
                  className="border-2"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="h-11 bg-[#0263cc] hover:bg-[#0263cc]/90 text-white font-semibold gap-2 shadow-md hover:shadow-lg transition-all"
                >
                  <Plus className="h-4 w-4" />
                  Create Vehicle
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Edit className="h-5 w-5 text-primary" />
                Edit Vehicle
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-5 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-vehicleNumber" className="text-right font-semibold flex items-center gap-2 justify-end">
                  <Bus className="h-4 w-4" />
                  Vehicle Number
                </Label>
                <Input
                  id="edit-vehicleNumber"
                  value={editForm.number}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, number: e.target.value }))}
                  className="col-span-3 h-11 border-2 focus:border-primary transition-colors"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-type" className="text-right font-semibold">
                  Type
                </Label>
                <Select 
                  value={editForm.type} 
                  onValueChange={(value) => setEditForm((prev) => ({ ...prev, type: value }))}
                >
                  <SelectTrigger className="col-span-3 h-11 border-2 focus:border-primary transition-colors">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bus">Bus</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-email" className="text-right font-semibold flex items-center gap-2 justify-end">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="col-span-3 h-11 border-2 focus:border-primary transition-colors"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-driver" className="text-right font-semibold flex items-center gap-2 justify-end">
                  <User className="h-4 w-4" />
                  Driver
                </Label>
                <Select 
                  value={editForm.driver || "none"} 
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, driver: value === "none" ? "" : value }))}
                >
                  <SelectTrigger className="col-span-3 h-11 border-2 focus:border-primary transition-colors">
                    <SelectValue placeholder="Select a driver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Driver</SelectItem>
                    {drivers
                      .filter(d => d.role === "driver")
                      .map(driver => (
                        <SelectItem key={driver._id} value={driver._id}>
                          {formatPersonName(driver)}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-conductor" className="text-right font-semibold flex items-center gap-2 justify-end">
                  <Users className="h-4 w-4" />
                  Conductor
                </Label>
                <Select 
                  value={editForm.conductor || "none"} 
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, conductor: value === "none" ? "" : value }))}
                >
                  <SelectTrigger className="col-span-3 h-11 border-2 focus:border-primary transition-colors">
                    <SelectValue placeholder="Select a conductor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Conductor</SelectItem>
                    {drivers
                      .filter(d => d.role === "conductor")
                      .map(conductor => (
                        <SelectItem key={conductor._id} value={conductor._id}>
                          {formatPersonName(conductor)}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>
            {error && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setEditModalOpen(false);
                setError("");
              }} className="border-2">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-primary hover:bg-primary/90 gap-2">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={passwordModalOpen} onOpenChange={(open) => {
          setPasswordModalOpen(open);
          if (!open) {
            setChangingPasswordVehicle(null);
            setNewPassword("");
            setConfirmPassword("");
            setPasswordError("");
            setShowNewPassword(false);
            setShowConfirmPassword(false);
          }
        }}>
          <DialogContent className="max-w-md border-2 shadow-xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                Change Vehicle Password
              </DialogTitle>
            </DialogHeader>
            {changingPasswordVehicle && (
              <div className="space-y-4 py-4">
                <div className="p-3 rounded-lg bg-muted/50 border">
                  <p className="text-sm text-muted-foreground mb-1">Vehicle</p>
                  <p className="font-semibold flex items-center gap-2">
                    <Bus className="h-4 w-4 text-primary" />
                    {changingPasswordVehicle.vehicleNumber}
                  </p>
                </div>
                
                {passwordError && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <p className="text-sm text-red-600 font-medium">{passwordError}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-sm font-semibold">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-11 border-2 focus:border-primary transition-colors pr-10"
                      placeholder="Enter new password"
                      disabled={changingPassword}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-11 w-10 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      disabled={changingPassword}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-sm font-semibold">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-11 border-2 focus:border-primary transition-colors pr-10"
                      placeholder="Confirm new password"
                      disabled={changingPassword}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !changingPassword) {
                          handleSavePassword();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-11 w-10 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={changingPassword}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Password must be at least 6 characters long
                </p>
              </div>
            )}
            <DialogFooter>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setPasswordModalOpen(false);
                  setChangingPasswordVehicle(null);
                  setNewPassword("");
                  setConfirmPassword("");
                  setPasswordError("");
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                }}
                className="border-2"
                disabled={changingPassword}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSavePassword} 
                disabled={changingPassword || !newPassword || !confirmPassword}
                className="h-11 bg-[#0263cc] hover:bg-[#0263cc]/90 text-white font-semibold gap-2 shadow-md hover:shadow-lg transition-all"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4" />
                    Change Password
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default VehicleManagement;
